import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import { AnimatePresence, motion } from 'framer-motion';

/**
 * Modal — upgraded in place (same API: {isOpen, close, title, children, size?}).
 *
 * Improvements over the previous version:
 *   - Rendered via portal, correct layering regardless of parent stacking context.
 *   - Framer-motion scale-in + backdrop fade (≤200ms).
 *   - Esc-to-close + body-scroll lock while open.
 *   - New design tokens (e3 shadow, rounded-xl, ink borders).
 *   - Backdrop blur for depth.
 *   - `size` optional prop: 'sm' | 'md' | 'lg' (defaults to 'md').
 */
const SIZES = {
  sm: 'max-w-[440px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[720px]',
};

export default function Modal({ children, isOpen, close, title, size = 'md' }) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close?.();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, close]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={title}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            onClick={close}
            className="absolute inset-0 bg-ink-950/50 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 2 }}
            transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
            className={`relative w-full ${SIZES[size] || SIZES.md} max-h-[calc(100vh-4rem)] overflow-y-auto rounded-xl bg-surface shadow-e3 ring-1 ring-line/60`}
          >
            {title !== undefined && (
              <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-line bg-surface/95 px-5 py-4 backdrop-blur">
                <p className="text-h2 text-fg">{title}</p>
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-ink-100 hover:text-fg"
                >
                  <FiX size={18} />
                </button>
              </div>
            )}
            <div className="p-5">{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
